import { useCallback, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { EscalatedSubject, vnextApiClient } from '@/services/vnext-api';
import { SubjectScopeType } from '@/types';

/**
 * Escalate report subjects to GDM-3G (automated project + Google Docs
 * export). The sole survivor of the retired Subjects page's `useSubjects`
 * hook — everything else there was UI for browsing/editing subjects,
 * which the Reports page superseded.
 */
export function useSubjectEscalation() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const escalateSubjects = useCallback(
    async (
      subjects: { id: string; name: string; scope: SubjectScopeType; report_id?: string }[]
    ): Promise<EscalatedSubject[]> => {
      try {
        setIsLoading(true);
        setError(null);

        const escalatedSubjects = await vnextApiClient.escalateSubjects(subjects);

        notifications.show({
          title: 'Escalation Started',
          message: `Started escalation for ${subjects.length} subject${subjects.length > 1 ? 's' : ''}`,
          color: 'blue',
        });

        return escalatedSubjects;
      } catch (err) {
        const escalationError =
          err instanceof Error ? err : new Error('Failed to escalate subjects');
        setError(escalationError);

        notifications.show({
          title: 'Error',
          message: escalationError.message,
          color: 'red',
        });

        throw escalationError;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { escalateSubjects, isLoading, error };
}
